import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import "../css/publish.css";
import RouteMap from "../loads/RouteMap";
import { attachAutocomplete, geocode, routeInfo } from "../loads/geo";
import {
  Icon,
  P,
  API,
  authHeaders,
  BODY_TYPE_OPTIONS,
  ORDER_TYPES,
  PACKAGE_TYPES,
  LOAD_TYPES,
  CURRENCY_OPTIONS,
  currencyLabel,
  codeSign,
  cityOf,
  isoDate,
  parseDate,
  fmtDateShort,
  nf,
} from "../loads/common";
import { transportBodyTypes } from "../../components/search/categories";
import { fetchTrucks, fetchMarket, sendToTrucks } from "../../utils/matchingApi";
import { extractOrderEntities } from "../../utils/aiAssistant";
import { Dialog, useToast } from "../fleet/ui";
import { apiError } from "../loads/orderActions";

/**
 * «Опубликовать груз» — wireframe iTruck_Publish_Load.html.
 * Payload/validation/geocoding ported from legacy components/orders/OrderForm.jsx
 * (POST /orders/, status 1 = published, 5 = draft). The right «Подходящие машины»
 * panel is the live matching market: before publishing it is derived from live
 * trucks (GET /m/trucks), right after publishing it is GET /m/orders/:id/market.
 */
const MIC = (
  <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" viewBox="0 0 24 24">
    <rect height="13" rx="3" width="6" x="9" y="2"></rect>
    <path d="M5 10v2a7 7 0 0 0 14 0v-2 M12 19v3 M9 22h6"></path>
  </svg>
);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const FLEX = { "Точная дата": 0, "± 1 день": 1, "± 2 дня": 2, "± 3 дня": 3, "± 7 дней": 7 };
const today = () => isoDate(new Date());
const foundMachines = (n) => {
  const a = n % 10;
  const b = n % 100;
  if (a === 1 && b !== 11) return `Найдена ${n} машина`;
  if (a >= 2 && a <= 4 && (b < 12 || b > 14)) return `Найдено ${n} машины`;
  return `Найдено ${n} машин`;
};

const availLabel = (iso) => {
  const d = parseDate(iso);
  if (!d) return "—";
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()) - t) / 86400000);
  if (diff <= 0) return "Сегодня";
  if (diff === 1) return "Завтра";
  return fmtDateShort(iso);
};

const truckLine = (t) =>
  [
    t.max_load_weight != null ? `${nf(t.max_load_weight)} т` : null,
    t.max_load_volume != null ? `${nf(t.max_load_volume)} м³` : null,
    t.body_type_name || null,
  ]
    .filter(Boolean)
    .join(" • ");

const mTrucksAll = (m) => m?.market?.top_trucks || [];

/** An existing order → the form's fields (for «Редактировать» / «Повторить груз»). */
const orderToForm = (o, { keepDate }) => {
  const days = (() => {
    const a = parseDate(o.from_date);
    const b = parseDate(o.to_date);
    return a && b ? Math.round((b - a) / 86400000) : 0;
  })();
  const flex = Object.entries(FLEX).find(([, n]) => n === days)?.[0] || "Точная дата";
  const str = (v) => (v == null ? "" : String(v));
  return {
    origin: o.from_address || "",
    destination: o.to_address || "",
    date: keepDate ? o.from_date || "" : "",
    flexibility: flex,
    cargo_type: str(o.item?.type),
    package_type: str(o.package_type),
    body: str(o.transport_body_type),
    weight: str(o.item?.weight),
    volume: str(o.item?.volume),
    loading: str(Array.isArray(o.load_type) ? o.load_type[0] : ""),
    price_mode: o.price ? "fixed" : "negotiable",
    price: str(o.price),
    currency: str(o.currency || 4),
    vat: !!o.nds,
    comment: o.comment || "",
    person: o.contacts?.contact_name || "",
    phone: o.contacts?.phone_number || "",
    company: o.contacts?.company_name || "",
    email: o.contacts?.another_contacts || "",
  };
};

export default function PublishPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const repeatId = params.get("repeat");
  const sourceId = editId || repeatId;
  const user = useSelector((s) => s.auth?.user);
  const company = user?.profile?.company_profile?.company_name || "";
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  const [f, setF] = useState({
    origin: "",
    destination: "",
    date: "",
    flexibility: "± 2 дня",
    cargo_type: "",
    package_type: "",
    body: "",
    weight: "",
    volume: "",
    loading: "",
    price_mode: "fixed",
    price: "",
    currency: "4",
    vat: false,
    comment: "",
    person: fullName,
    phone: user?.phone_number || "",
    company,
    email: user?.email || "",
  });
  const [geo, setGeo] = useState({ from: null, to: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [published, setPublished] = useState(null); // created order
  const [market, setMarket] = useState(null);
  const [marketErr, setMarketErr] = useState(null);
  const [matching, setMatching] = useState(false);
  const [trucks, setTrucks] = useState(null);
  const [sent, setSent] = useState({}); // match_id -> true
  const [sentAll, setSentAll] = useState(null);
  const [sending, setSending] = useState(false);
  const [dlg, setDlg] = useState(null); // {k: help|smart|map|request|done|search, ...}
  const [hideSmart, setHideSmart] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [source, setSource] = useState(null); // order being edited / repeated
  const [sourceErr, setSourceErr] = useState(null);
  const [reqBusy, setReqBusy] = useState(false);
  const [toast, toastNode] = useToast();
  const formRef = useRef(null);
  const originRef = useRef(null);
  const destRef = useRef(null);

  // contacts default once the profile arrives
  useEffect(() => {
    setF((p) => ({
      ...p,
      person: p.person || fullName,
      phone: p.phone || user?.phone_number || "",
      company: p.company || company,
      email: p.email || user?.email || "",
    }));
  }, [fullName, company, user]);

  // «Редактировать» / «Повторить груз» from «Мои грузы»: the same full form, prefilled
  useEffect(() => {
    if (!sourceId) return undefined;
    let alive = true;
    axios
      .get(`${API}/orders/${sourceId}/`, { headers: authHeaders() })
      .then(({ data: o }) => {
        if (!alive) return;
        if (editId && o.owner !== user?.id) return setSourceErr("Редактировать можно только свои грузы.");
        if (editId && !(o.status === 1 || o.status === 5)) return setSourceErr("Этот груз уже в работе или закрыт — изменить его нельзя.");
        if (editId && o.driver) return setSourceErr("Перевозчик уже выбран — изменить груз нельзя.");
        setSource(o);
        setF((p) => ({ ...p, ...orderToForm(o, { keepDate: !!editId }) }));
        setGeo({
          from: o.location_from?.lat != null ? { lat: o.location_from.lat, lng: o.location_from.lng, country: o.from_country, country_code: o.from_country_code } : null,
          to: o.location_to?.lat != null ? { lat: o.location_to.lat, lng: o.location_to.lng, country: o.to_country, country_code: o.to_country_code } : null,
        });
        if (editId) setDraftId(null);
      })
      .catch(() => alive && setSourceErr("Не удалось загрузить груз."));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, user?.id]);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF((p) => ({ ...p, [k]: v }));
    if (k === "origin") setGeo((g) => ({ ...g, from: null }));
    if (k === "destination") setGeo((g) => ({ ...g, to: null }));
  };

  // Places autocomplete (Google Maps JS API is loaded by AuthProvider)
  useEffect(() => {
    let off1 = () => {};
    let off2 = () => {};
    let tries = 0;
    const attach = () => {
      if (!(window.google && window.google.maps && window.google.maps.places)) {
        if (tries++ < 20) setTimeout(attach, 500);
        return;
      }
      off1 = attachAutocomplete(originRef.current, (p) => {
        setF((x) => ({ ...x, origin: p.address }));
        setGeo((g) => ({ ...g, from: p }));
      });
      off2 = attachAutocomplete(destRef.current, (p) => {
        setF((x) => ({ ...x, destination: p.address }));
        setGeo((g) => ({ ...g, to: p }));
      });
    };
    attach();
    return () => {
      off1();
      off2();
    };
  }, []);

  // live trucks for the pre-publish preview
  useEffect(() => {
    let alive = true;
    fetchTrucks()
      .then((r) => alive && setTrucks(r?.trucks || []))
      .catch(() => alive && setTrucks([]));
    return () => {
      alive = false;
    };
  }, []);

  const bodyName = useMemo(
    () => transportBodyTypes.find((b) => b.id === Number(f.body))?.name || "",
    [f.body],
  );

  const preview = useMemo(() => {
    if (!trucks) return [];
    const city = cityOf(f.origin).toLowerCase();
    return trucks.filter((t) => {
      if (t.status && t.status !== "ACTIVE") return false;
      if (bodyName && t.body_type_name && !t.body_type_name.toLowerCase().startsWith(bodyName.toLowerCase().slice(0, 4))) return false;
      if (f.weight && t.max_load_weight != null && t.max_load_weight < Number(f.weight)) return false;
      if (f.volume && t.max_load_volume != null && t.max_load_volume < Number(f.volume)) return false;
      if (city && city !== "—" && t.city && !(t.city.toLowerCase().includes(city) || city.includes(t.city.toLowerCase()))) return false;
      return true;
    });
  }, [trucks, bodyName, f.weight, f.volume, f.origin]);

  const loadMarket = useCallback(async (id, poll = false) => {
    setMarketErr(null);
    // Django -> outbox -> matcher is asynchronous: right after publish the first answers are empty
    const attempts = poll ? 12 : 2;
    let last = null;
    if (poll) setMatching(true);
    for (let i = 0; i < attempts; i++) {
      try {
        last = await fetchMarket(id);
        if (!poll || (last?.market?.matched_trucks ?? 0) > 0 || i === attempts - 1) {
          setMarket(last);
          if (!poll || (last?.market?.matched_trucks ?? 0) > 0) break;
        }
      } catch (e) {
        if (i === attempts - 1 && !last) setMarketErr("Не удалось получить подбор машин");
      }
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 2000));
    }
    if (poll) setMatching(false);
    return last;
  }, []);

  const validate = () => {
    const miss = [];
    if (!f.origin.trim()) miss.push("Откуда");
    if (!f.destination.trim()) miss.push("Куда");
    if (!f.date) miss.push("Дата погрузки");
    if (!f.cargo_type) miss.push("Тип груза");
    if (!f.package_type) miss.push("Вид упаковки");
    if (!f.body) miss.push("Тип кузова");
    if (!f.weight) miss.push("Тоннаж");
    if (!f.volume) miss.push("Объём");
    if (!f.loading) miss.push("Способ погрузки");
    if (f.price_mode === "fixed" && !f.price) miss.push("Ставка");
    return miss;
  };

  const buildPayload = async (status, over = {}) => {
    const g = { ...f, ...over };
    let from = geo.from;
    let to = geo.to;
    if (!from && g.origin.trim()) from = await geocode(g.origin);
    if (!to && g.destination.trim()) to = await geocode(g.destination);
    const info = from && to ? await routeInfo(from, to) : {};
    let to_date = null;
    if (g.date) {
      const d = parseDate(g.date);
      d.setDate(d.getDate() + (FLEX[g.flexibility] ?? 0));
      to_date = isoDate(d);
    }
    const body = g.body ? transportBodyTypes.find((b) => b.id === Number(g.body)) : null;
    const parent = body && transportBodyTypes.find((b) => b.id === body.parent_id);
    const fixed = g.price_mode === "fixed" && g.price;
    const num = (v) => (v === "" || v == null ? null : Number(v));
    return {
      status,
      from_address: g.origin,
      to_address: g.destination,
      from_date: g.date || null,
      to_date,
      location_from: from ? { lat: from.lat, lng: from.lng } : {},
      location_to: to ? { lat: to.lat, lng: to.lng } : {},
      from_country: info.from_country || from?.country || null,
      from_country_code: info.from_country_code || from?.country_code || null,
      to_country: info.to_country || to?.country || null,
      to_country_code: info.to_country_code || to?.country_code || null,
      distance: info.distance || null,
      route_polyline: info.route_polyline || null,
      transport_category: parent?.category?.[0] ?? null,
      transport_body_type: num(g.body),
      package_type: num(g.package_type),
      load_type: g.loading ? [Number(g.loading)] : [],
      item: {
        type: num(g.cargo_type),
        weight: num(g.weight),
        volume: num(g.volume),
      },
      price: fixed ? Number(g.price) : null,
      currency: fixed ? Number(g.currency) : null,
      nds: fixed ? !!g.vat : false,
      comment: g.comment || null,
      contacts: {
        company_name: g.company || null,
        contact_name: g.person || null,
        phone_number: g.phone || null,
        another_contacts: g.email || null,
      },
    };
  };

  /** POST a new order, or PATCH the draft this page already saved. */
  const saveOrder = async (status, over) => {
    const payload = await buildPayload(status, over);
    const targetId = editId || draftId;
    const { data } = targetId
      ? await axios.patch(`${API}/orders/${targetId}/`, payload, { headers: authHeaders() })
      : await axios.post(`${API}/orders/`, payload, { headers: authHeaders() });
    setDraftId(editId ? null : status === 5 ? data.uuid : null);
    return { data, payload };
  };

  const afterPublish = ({ data, payload }) => {
    setPublished(data);
    setGeo({
      from: payload.location_from?.lat ? payload.location_from : null,
      to: payload.location_to?.lat ? payload.location_to : null,
    });
    loadMarket(data.uuid, true);
  };

  const submit = async () => {
    setError(null);
    const miss = validate();
    if (miss.length) {
      setError(`Заполните обязательные поля: ${miss.join(", ")}`);
      return;
    }
    setBusy(true);
    try {
      const r = await saveOrder(1);
      if (editId) {
        toast("Изменения сохранены");
        navigate("/my-orders");
        return;
      }
      afterPublish(r);
      setDlg({ k: "done", order: r.data });
    } catch (e) {
      setError(apiError(e, "Не удалось сохранить груз. Попробуйте ещё раз."));
    } finally {
      setBusy(false);
    }
  };

  // wireframe «Сохранить в черновики»: no validation, toast, stay on the page
  const saveDraft = async () => {
    setError(null);
    setBusy(true);
    try {
      await saveOrder(5);
      toast("Черновик сохранён");
      if (editId) navigate("/my-orders?tab=draft");
    } catch (e) {
      setError(apiError(e, "Не удалось сохранить черновик. Попробуйте ещё раз."));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setPublished(null);
    setMarket(null);
    setSent({});
    setSentAll(null);
    setDraftId(null);
    setGeo({ from: null, to: null });
    setError(null);
    setF((p) => ({ ...p, origin: "", destination: "", date: "", cargo_type: "", package_type: "", body: "", weight: "", volume: "", loading: "", price: "", comment: "" }));
  };

  const sendAll = async () => {
    setSending(true);
    try {
      const r = await sendToTrucks(published.uuid, {});
      setSentAll(r?.sent ?? r?.count ?? true);
      toast("Заявки отправлены подходящим машинам");
      loadMarket(published.uuid);
    } catch (e) {
      setError(apiError(e, "Не удалось отправить заявку"));
    } finally {
      setSending(false);
    }
  };

  // «Отправить заявку» on a truck card: wireframe validates the form, then asks route + phone
  const openRequest = (t) => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    if (!published) {
      const miss = validate();
      if (miss.length) {
        setError(`Заполните обязательные поля: ${miss.join(", ")}`);
        return;
      }
    }
    setDlg({ k: "request", t });
  };

  const doRequest = async (t, phone) => {
    setReqBusy(true);
    try {
      let order = published;
      if (!order) {
        const r = await saveOrder(1, { phone });
        setF((p) => ({ ...p, phone }));
        afterPublish(r);
        order = r.data;
      }
      let match = null;
      for (let i = 0; i < 12 && !match; i++) {
        if (i) await sleep(2000);
        const m = await fetchMarket(order.uuid).catch(() => null);
        if (m) setMarket(m);
        match = (m?.market?.top_trucks || []).find((x) => Number(x.vehicle_id) === Number(t.vehicle_id));
      }
      setDlg(null);
      if (!match) {
        toast("Эта машина пока не подходит под параметры груза — заявка не отправлена. Груз опубликован.");
        return;
      }
      await sendToTrucks(order.uuid, { match_ids: [match.match_id] });
      setSent((s) => ({ ...s, [t.vehicle_id]: true }));
      toast(`Заявка отправлена: ${t.mark || "машина"}`);
      loadMarket(order.uuid);
    } catch (e) {
      setDlg(null);
      toast(apiError(e, "Не удалось отправить заявку"));
    } finally {
      setReqBusy(false);
    }
  };

  const clearBtn = (k, label) =>
    f[k] ? (
      <button
        aria-label={`Очистить ${label}`}
        className="clear"
        type="button"
        onClick={() => {
          setF((p) => ({ ...p, [k]: "" }));
          if (k === "origin") setGeo((g) => ({ ...g, from: null }));
          if (k === "destination") setGeo((g) => ({ ...g, to: null }));
        }}
      >
        <Icon d={P.x} />
      </button>
    ) : null;

  const swap = () => {
    setF((p) => ({ ...p, origin: p.destination, destination: p.origin }));
    setGeo((g) => ({ from: g.to, to: g.from }));
  };

  // Умное заполнение (ported from legacy AiAutofill → POST {LLM_PROXY}/api/llm/order-autofill)
  const runSmart = async (text) => {
    const d = await extractOrderEntities(text);
    if (!d) throw new Error("empty");
    const upd = {};
    const g = {};
    if (d.fromLocation) {
      const p = await geocode(d.fromLocation);
      upd.origin = p?.address || d.fromLocation;
      if (p) g.from = p;
    }
    if (d.toLocation) {
      const p = await geocode(d.toLocation);
      upd.destination = p?.address || d.toLocation;
      if (p) g.to = p;
    }
    if (d.fromDate) {
      upd.date = d.fromDate < today() ? today() : d.fromDate;
      if (d.toDate) {
        const diff = Math.round((parseDate(d.toDate) - parseDate(d.fromDate)) / 86400000);
        const key = Object.keys(FLEX).find((k) => FLEX[k] === diff);
        if (key) upd.flexibility = key;
      }
    }
    if (d.weight != null) upd.weight = String(d.weight);
    if (d.volume != null) upd.volume = String(d.volume);
    if (d.type != null && ORDER_TYPES[d.type]) upd.cargo_type = String(d.type);
    if (d.package_type != null && PACKAGE_TYPES[d.package_type]) upd.package_type = String(d.package_type);
    const lt = Array.isArray(d.load_type) ? d.load_type.find((x) => LOAD_TYPES[x]) : null;
    if (lt) upd.loading = String(lt);
    if (d.price != null) {
      upd.price_mode = "fixed";
      upd.price = String(d.price);
      if (d.currency && CURRENCY_OPTIONS.some(([k]) => k === String(d.currency))) upd.currency = String(d.currency);
      if (d.nds != null) upd.vat = !!d.nds;
    }
    if (d.comment) upd.comment = String(d.comment).slice(0, 1000);
    if (d.bodyType) {
      const nm = String(d.bodyType).toLowerCase();
      const b = BODY_TYPE_OPTIONS.find((x) => x.name.toLowerCase() === nm || nm.includes(x.name.toLowerCase()));
      if (b) upd.body = String(b.id);
    }
    if (!Object.keys(upd).length) throw new Error("nothing");
    setF((p) => ({ ...p, ...upd }));
    setGeo((p) => ({ ...p, ...g }));
  };

  // wireframe: Enter in the global search → «Поиск машин» dialog over the visible truck cards
  const cardsRef = useRef([]);
  cardsRef.current = published ? mTrucksAll(market) : preview;
  useEffect(() => {
    const onSearch = (e) => {
      e.preventDefault();
      setDlg({ k: "search", q: (e.detail?.q || "").toLowerCase() });
    };
    window.addEventListener("cab:global-search", onSearch);
    return () => window.removeEventListener("cab:global-search", onSearch);
  }, []);

  const locked = !!published || !!sourceErr;
  const mTrucks = market?.market?.top_trucks || [];
  // free trucks drawn on the map and listed beside the full-screen map
  const mapTrucks = useMemo(
    () => (published ? market?.market?.top_trucks || [] : preview).map((t) => ({ ...t, title: [t.mark, t.city].filter(Boolean).join(" · ") })),
    [published, market, preview],
  );
  const matched = market?.market?.matched_trucks;

  return (
    <CabPage name="publish">
      <main id="main">
        <div className="publish-layout">
          <div className="publish-left">
            <header className="publish-heading">
              <div>
                <h1>{editId ? `Редактировать груз #${String(editId).toUpperCase()}` : repeatId ? "Повторить груз" : "Опубликовать груз"}</h1>
                <p>Заполните информацию о грузе, и подходящие перевозчики увидят ваше предложение</p>
              </div>
              <button className="button help" type="button" onClick={() => setDlg({ k: "help" })}>
                ▶ Как это работает?
              </button>
            </header>
            {!hideSmart && (
              <section className="smart-banner">
                <div>
                  <h2>
                    <span>✧</span> Умное заполнение <small>Бета</small>
                  </h2>
                  <p>
                    Загрузите документы, вставьте текст или просто опишите груз голосом — мы автоматически
                    <br />
                    заполним поля и подберём подходящие параметры.
                  </p>
                </div>
                <button className="dictate" type="button" onClick={() => setDlg({ k: "smart" })}>
                  <span className="mic">{MIC}</span>
                  <span>
                    <strong>Продиктовать груз</strong>
                    <small>
                      Например: «Пластиковые трубы
                      <br />
                      Москва — Бишкек, 20 тонн...»
                    </small>
                  </span>
                </button>
                <button aria-label="Скрыть умное заполнение" className="close-smart" type="button" onClick={() => setHideSmart(true)}>
                  <Icon d={P.x} />
                </button>
              </section>
            )}
            <form
              id="publish-form"
              ref={formRef}
              onSubmit={(e) => {
                e.preventDefault();
                if (!locked) submit();
              }}
            >
              <fieldset disabled={locked || busy} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                <section className="form-panel route-panel">
                  <h2>
                    <Icon d={P.pin} />
                    Маршрут
                  </h2>
                  <div className="route-fields">
                    <label className="field">
                      <span>Откуда</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.pin} />
                        <input ref={originRef} aria-label="Откуда" name="origin" required type="text" value={f.origin} onChange={set("origin")} autoComplete="off" />
                        {clearBtn("origin", "Откуда")}
                      </span>
                    </label>
                    <button aria-label="Поменять маршрут местами" className="swap" type="button" onClick={swap}>
                      ⇄
                    </button>
                    <label className="field">
                      <span>Куда</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.pin} />
                        <input ref={destRef} aria-label="Куда" name="destination" required type="text" value={f.destination} onChange={set("destination")} autoComplete="off" />
                        {clearBtn("destination", "Куда")}
                      </span>
                    </label>
                  </div>
                  <div className="date-fields">
                    <label className="field">
                      <span>Дата погрузки</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.cal} />
                        <input aria-label="Дата погрузки" name="date" required type="date" min={today()} value={f.date} onChange={set("date")} />
                        {clearBtn("date", "Дата погрузки")}
                      </span>
                    </label>
                    <label className="field">
                      <span>Сроки (необязательно)</span>
                      <span className="select-wrap with-icon">
                        <Icon d={P.cal} />
                        <select aria-label="Сроки (необязательно)" name="flexibility" value={f.flexibility} onChange={set("flexibility")}>
                          {Object.keys(FLEX).map((k) => (
                            <option key={k}>{k}</option>
                          ))}
                        </select>
                        <Icon d={P.chevron} />
                      </span>
                    </label>
                  </div>
                </section>

                <section className="form-panel cargo-panel">
                  <h2>
                    <Icon d={P.box} />
                    Информация о грузе
                  </h2>
                  <div className="cargo-first">
                    <label className="field">
                      <span>Тип груза</span>
                      <span className="select-wrap with-icon">
                        <Icon d={P.weight} />
                        <select aria-label="Тип груза" name="cargo_type" required value={f.cargo_type} onChange={set("cargo_type")}>
                          <option value="">Выберите</option>
                          {Object.entries(ORDER_TYPES).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                        <Icon d={P.chevron} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Вид упаковки</span>
                      <span className="select-wrap with-icon">
                        <Icon d={P.box} />
                        <select aria-label="Вид упаковки" name="package_type" required value={f.package_type} onChange={set("package_type")}>
                          <option value="">Выберите</option>
                          {Object.entries(PACKAGE_TYPES).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                        <Icon d={P.chevron} />
                      </span>
                    </label>
                    <button aria-label="Описать груз" className="voice-button" type="button" onClick={() => setDlg({ k: "smart" })}>
                      {MIC}
                    </button>
                  </div>
                  <div className="cargo-second">
                    <label className="field">
                      <span>Тип кузова</span>
                      <span className="select-wrap with-icon">
                        <Icon d={P.truck} />
                        <select aria-label="Тип кузова" name="body" required value={f.body} onChange={set("body")}>
                          <option value="">Выберите</option>
                          {BODY_TYPE_OPTIONS.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </select>
                        <Icon d={P.chevron} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Тоннаж</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.weight} />
                        <input aria-label="Тоннаж" min="0" name="weight" required step="any" type="number" value={f.weight} onChange={set("weight")} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Объём</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.box} />
                        <input aria-label="Объём" min="0" name="volume" required step="any" type="number" value={f.volume} onChange={set("volume")} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Способ погрузки</span>
                      <span className="select-wrap with-icon">
                        <Icon d={P.box} />
                        <select aria-label="Способ погрузки" name="loading" required value={f.loading} onChange={set("loading")}>
                          <option value="">Выберите</option>
                          {Object.entries(LOAD_TYPES).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                        <Icon d={P.chevron} />
                      </span>
                    </label>
                  </div>
                </section>

                <section className="form-panel extra-panel">
                  <h2>
                    <Icon d={P.gear} />
                    Дополнительно
                  </h2>
                  <div className="pricing">
                    <fieldset className="price-modes">
                      <legend>Режим цены</legend>
                      <label>
                        <input checked={f.price_mode === "fixed"} name="price_mode" type="radio" value="fixed" onChange={set("price_mode")} />
                        <span>
                          <Icon d={P.card} />
                          <span>
                            Ставка<small>Фиксированная цена</small>
                          </span>
                        </span>
                      </label>
                      <label>
                        <input checked={f.price_mode === "negotiable"} name="price_mode" type="radio" value="negotiable" onChange={set("price_mode")} />
                        <span>
                          <Icon d={P.gear} />
                          <span>
                            Торг<small>Предлагайте свою цену</small>
                          </span>
                        </span>
                      </label>
                    </fieldset>
                    <label className="field price-field">
                      <span>Ставка</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.card} />
                        <input
                          aria-label="Ставка"
                          min="1"
                          name="price"
                          type="number"
                          disabled={f.price_mode !== "fixed"}
                          placeholder={f.price_mode !== "fixed" ? "Запрос ставки" : ""}
                          value={f.price_mode === "fixed" ? f.price : ""}
                          onChange={set("price")}
                        />
                        <select aria-label="Валюта" name="currency" disabled={f.price_mode !== "fixed"} value={f.currency} onChange={set("currency")}>
                          {CURRENCY_OPTIONS.map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </span>
                    </label>
                    <label className="vat">
                      <input checked={f.vat} name="vat" type="checkbox" disabled={f.price_mode !== "fixed"} onChange={set("vat")} />С НДС
                    </label>
                  </div>
                  <label className="comment field">
                    <span>
                      Комментарий к грузу <em>(необязательно)</em>
                    </span>
                    <textarea maxLength="1000" name="comment" value={f.comment} onChange={set("comment")} />
                    <small id="comment-count">{f.comment.length}/1000</small>
                  </label>
                  <h2 className="contact-title">
                    <Icon d={P.user} />
                    Контактная информация
                  </h2>
                  <div className="contact-fields">
                    <label className="field">
                      <span>Контактное лицо</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.user} />
                        <input aria-label="Контактное лицо" name="person" required type="text" value={f.person} onChange={set("person")} />
                        {clearBtn("person", "Контактное лицо")}
                      </span>
                    </label>
                    <label className="field">
                      <span>Телефон</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.globe} />
                        <input aria-label="Телефон" name="phone" required type="tel" value={f.phone} onChange={set("phone")} />
                        {clearBtn("phone", "Телефон")}
                      </span>
                    </label>
                    <label className="field">
                      <span>Компания</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.box} />
                        <input aria-label="Компания" name="company" type="text" value={f.company} onChange={set("company")} />
                      </span>
                    </label>
                    <label className="field">
                      <span>Дополнительно (email, мессенджер)</span>
                      <span className="input-wrap with-icon">
                        <Icon d={P.chat} />
                        <input aria-label="Дополнительно (email, мессенджер)" name="email" type="text" value={f.email} onChange={set("email")} />
                      </span>
                    </label>
                  </div>
                  <div className="publish-footer">
                    <button className="button primary" type="submit">
                      <Icon d={P.arrow} />
                      {busy ? (editId ? "Сохраняем…" : "Публикуем…") : editId ? "Сохранить изменения" : "Опубликовать груз"}
                    </button>
                    <button className="button draft" type="button" disabled={locked || busy} onClick={saveDraft}>
                      <Icon d={P.weight} />
                      Сохранить в черновики
                    </button>
                    <p>Груз увидят перевозчики с подходящими машинами</p>
                  </div>
                </section>
              </fieldset>
              {sourceErr && (
                <p className="form-note" role="alert" style={{ color: "#d6303c", fontSize: 12 }}>
                  {sourceErr}{" "}
                  <button type="button" style={{ color: "#0063ff", fontSize: 12 }} onClick={() => navigate("/my-orders")}>
                    Мои грузы
                  </button>
                </p>
              )}
              {repeatId && source && !published && (
                <p className="form-note" style={{ color: "#536b9c", fontSize: 12 }}>
                  Данные скопированы из груза #{String(source.uuid).toUpperCase()}. Укажите новую дату погрузки и опубликуйте.
                </p>
              )}
              {error && (
                <p className="form-note" role="alert" style={{ color: "#d6303c", fontSize: 12 }}>
                  {error}
                </p>
              )}
              {published && (
                <p className="form-note" style={{ color: "#00a34f", fontSize: 12 }}>
                  Груз №{String(published.uuid).toUpperCase()} опубликован.{" "}
                  <button type="button" style={{ color: "#0063ff", fontSize: 12 }} onClick={() => navigate("/my-orders")}>
                    Мои грузы
                  </button>
                  {" · "}
                  <button type="button" style={{ color: "#0063ff", fontSize: 12 }} onClick={reset}>
                    Опубликовать ещё один
                  </button>
                </p>
              )}
            </form>
          </div>

          <aside className="matches">
            <header>
              <h2>
                <Icon d={P.truck} />
                Подходящие машины
              </h2>
              <span>{published ? (market && !matching ? foundMachines(matched ?? 0) : "Подбираем…") : trucks ? foundMachines(preview.length) : "…"}</span>
            </header>
            <p>
              {published
                ? market && !matching
                  ? `Свежих: ${market.market.fresh_trucks ?? 0} • средний балл совпадения ${market.market.avg_score ?? 0}%`
                  : marketErr || "Идёт подбор машин для вашего груза"
                : "Свободные машины на вашем маршруте"}
            </p>
            <div className="route-map">
              <RouteMap from={geo.from} to={geo.to} trucks={mapTrucks} />
              <button type="button" onClick={() => setDlg({ k: "map" })}>
                <Icon d={P.expand} />
                Открыть карту на весь экран
              </button>
            </div>
            <div className="match-list">
              {(published ? mTrucks : preview.slice(0, 5)).map((t) => (
                <article className="match-card" key={t.match_id || t.vehicle_id}>
                  <span className="truck-photo" style={{ display: "grid", placeItems: "center", color: "#0063ff" }}>
                    <Icon d={P.truck} style={{ width: 32, height: 32 }} />
                  </span>
                  <div className="match-info">
                    <strong>{t.mark || "Машина"}</strong>
                    <small>{truckLine(t)}</small>
                    <div className="match-bottom">
                      <span className="rating">{published ? t.driver_name : t.city}</span>
                      <span className="available">
                        <i></i>
                        <span>
                          Свободен<small>{availLabel(t.available_from)}</small>
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="match-price">
                    {published ? (
                      <>
                        <b>{t.score}%</b>
                        <small>{t.distance_km != null ? `${nf(t.distance_km)} км от погрузки` : "совпадение"}</small>
                      </>
                    ) : t.min_price != null ? (
                      <>
                        <b>
                          {nf(t.min_price)} {codeSign(t.currency)}
                        </b>
                        <small>мин. ставка</small>
                      </>
                    ) : null}
                    <button className="button primary" type="button" disabled={!!sent[t.vehicle_id] || (published && !!sentAll)} onClick={() => openRequest(t)}>
                      {sent[t.vehicle_id] || (published && sentAll) ? "Отправлено" : "Отправить заявку"}
                    </button>
                  </div>
                </article>
              ))}
              {!published && trucks && preview.length === 0 && (
                <p style={{ fontSize: 12, color: "#6e84ab", padding: "12px 0" }}>
                  Пока нет свободных машин под этот груз. Измените маршрут или параметры.
                </p>
              )}
              {published && market && !matching && mTrucks.length === 0 && (
                <p style={{ fontSize: 12, color: "#6e84ab", padding: "12px 0" }}>Подходящих машин пока нет — груз виден перевозчикам, мы сообщим об откликах.</p>
              )}
            </div>
            {published && !matching && (matched ?? 0) > 0 && (
              <button
                className="button primary"
                type="button"
                disabled={sending || !!sentAll}
                onClick={sendAll}
                style={{ width: "100%", marginTop: 14, height: 42 }}
              >
                {sentAll ? "Заявки отправлены" : sending ? "Отправляем…" : "Отправить заявку подходящим машинам"}
              </button>
            )}
            <label className="notify-matches" title="Скоро: подписка на новые машины по маршруту пока недоступна">
              <Icon d={P.bell} />
              <span>
                Получать уведомления о новых подходящих
                <br />
                машинах на этом маршруте — скоро
              </span>
              <input disabled name="notify" role="switch" type="checkbox" />
              <i></i>
            </label>
          </aside>
        </div>
      </main>
      {dlg && (
        <PublishDialog
          dlg={dlg}
          f={f}
          geo={geo}
          published={published}
          cards={cardsRef.current}
          trucks={mapTrucks}
          onRequestTruck={openRequest}
          reqBusy={reqBusy}
          onClose={() => setDlg(null)}
          onDoRequest={doRequest}
          onComment={(text) => {
            setF((p) => ({ ...p, comment: text.slice(0, 1000) }));
            setDlg(null);
            toast("Описание добавлено в комментарий");
          }}
          onSmart={async (text) => {
            try {
              await runSmart(text);
              setDlg(null);
              toast("Форма заполнена по описанию");
            } catch (e) {
              toast(
                e.message === "nothing"
                  ? "Не удалось распознать параметры груза в описании"
                  : "Умное заполнение сейчас недоступно (сервис распознавания не отвечает)",
              );
            }
          }}
        />
      )}
      {toastNode}
      <MobileNav />
    </CabPage>
  );
}

function WideDialog({ title, onClose, full = false, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className={`modal${full ? " full" : ""}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section aria-labelledby="dialog-title" aria-modal="true" className="dialog map-dialog" role="dialog" style={full ? undefined : { width: 850 }}>
        <header>
          <h2 id="dialog-title">{title}</h2>
          <button aria-label="Закрыть" className="icon-button" onClick={onClose} type="button">
            <Icon d={P.x} />
          </button>
        </header>
        <div id="dialog-content">{children}</div>
      </section>
    </div>
  );
}

function SmartForm({ onSmart, onComment }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const rec = useRef(null);
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const r = new SR();
      r.continuous = true;
      r.interimResults = false;
      r.lang = "ru-RU";
      r.onresult = (ev) => {
        let t = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) if (ev.results[i].isFinal) t += ev.results[i][0].transcript;
        if (t) setText((p) => p + (p ? " " : "") + t);
      };
      r.onend = () => setListening(false);
      r.onerror = () => setListening(false);
      rec.current = r;
    }
    return () => rec.current && rec.current.stop();
  }, []);
  const dictate = () => {
    if (!rec.current) return;
    if (listening) rec.current.stop();
    else {
      try {
        rec.current.start();
        setListening(true);
      } catch (e) {
        /* already started */
      }
    }
  };
  return (
    <form
      id="smart-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        await onSmart(text);
        setBusy(false);
      }}
    >
      <label>
        Описание
        <textarea rows="5" required value={text} onChange={(e) => setText(e.target.value)} placeholder="Опишите груз и условия перевозки" />
      </label>
      <div className="actions">
        <button className="button primary" type="submit" disabled={busy}>
          {busy ? "Заполняем…" : "Заполнить форму"}
        </button>
        {typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition) ? (
          <button className="button" type="button" onClick={dictate}>
            {listening ? "Остановить запись" : "Продиктовать"}
          </button>
        ) : null}
        <button className="button" type="button" disabled={!text.trim()} onClick={() => onComment(text)}>
          Добавить в комментарий
        </button>
      </div>
    </form>
  );
}

function RequestForm({ t, f, published, reqBusy, onSubmit }) {
  const [phone, setPhone] = useState(f.phone);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(t, phone);
      }}
    >
      <label>
        Маршрут
        <input name="route" value={`${f.origin} → ${f.destination}`} readOnly />
      </label>
      <label>
        Контактный телефон
        <input name="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required disabled={!!published} />
      </label>
      {!published && <p>Груз будет опубликован, и заявка уйдёт выбранной машине.</p>}
      <div className="actions">
        <button className="button primary" type="submit" disabled={reqBusy}>
          {reqBusy ? "Отправляем…" : "Отправить заявку"}
        </button>
      </div>
    </form>
  );
}

function PublishDialog({ dlg, f, geo, published, cards, trucks, onRequestTruck, reqBusy, onClose, onDoRequest, onComment, onSmart }) {
  const route = [cityOf(f.origin), cityOf(f.destination)].filter((x) => x && x !== "—");
  if (dlg.k === "help")
    return (
      <Dialog title="Как опубликовать груз" onClose={onClose}>
        <p>1. Укажите маршрут и дату погрузки.</p>
        <p>2. Заполните характеристики, ставку и контакты.</p>
        <p>3. Опубликуйте груз или сохраните черновик.</p>
      </Dialog>
    );
  if (dlg.k === "smart")
    return (
      <Dialog title="Умное заполнение — Бета" onClose={onClose}>
        <SmartForm onSmart={onSmart} onComment={onComment} />
      </Dialog>
    );
  if (dlg.k === "map")
    return (
      <WideDialog full title={route.length ? `Маршрут ${route.join(" — ")}` : "Маршрут"} onClose={onClose}>
        <div className="pub-fullmap">
          <div className="route-map">
            <RouteMap from={geo.from} to={geo.to} trucks={trucks} onPick={onRequestTruck} />
          </div>
          <aside aria-label="Свободный транспорт" className="pub-fullmap-side">
            <h3>Свободный транспорт · {trucks.length}</h3>
            {trucks.length === 0 ? <p>Пока нет свободных машин под этот груз.</p> : null}
            {trucks.map((t) => (
              <article className="pub-truck" key={t.match_id || t.vehicle_id}>
                <div>
                  <strong>{[t.mark, t.government_number].filter(Boolean).join(" · ") || "Машина"}</strong>
                  <small>{truckLine(t) || "—"}</small>
                  <small>{[t.city, published ? (t.distance_km != null ? `${nf(t.distance_km)} км от погрузки` : null) : t.min_price != null ? `от ${nf(t.min_price)} ${codeSign(t.currency)}` : null].filter(Boolean).join(" · ")}</small>
                </div>
                <button className="button primary" onClick={() => onRequestTruck(t)} type="button">
                  Отправить заявку
                </button>
              </article>
            ))}
          </aside>
        </div>
      </WideDialog>
    );
  if (dlg.k === "request")
    return (
      <Dialog title={`Заявка — ${dlg.t.mark || "машина"}`} onClose={onClose}>
        <RequestForm t={dlg.t} f={f} published={published} reqBusy={reqBusy} onSubmit={onDoRequest} />
      </Dialog>
    );
  if (dlg.k === "done") {
    const o = dlg.order;
    return (
      <Dialog title="Груз опубликован" onClose={onClose}>
        <p>
          <strong>
            {f.origin} → {f.destination}
          </strong>
        </p>
        <p>
          {ORDER_TYPES[o.item?.type] || "Груз"} · {f.weight} т · {f.volume} м³
        </p>
        {o.price ? (
          <p>
            {Number(o.price).toLocaleString("ru-RU")} {currencyLabel(Number(o.currency))}
          </p>
        ) : null}
        <button className="button primary" type="button" onClick={onClose}>
          Готово
        </button>
      </Dialog>
    );
  }
  if (dlg.k === "search") {
    const found = cards.filter((c) => [c.mark, c.government_number, c.city, c.driver_name, c.body_type_name].filter(Boolean).join(" ").toLowerCase().includes(dlg.q));
    return (
      <Dialog title="Поиск машин" onClose={onClose}>
        {found.length ? found.map((c) => <p key={c.match_id || c.vehicle_id}>{c.mark || "Машина"}{c.government_number ? ` · ${c.government_number}` : ""}</p>) : <p>Совпадений среди подходящих машин нет.</p>}
      </Dialog>
    );
  }
  return null;
}
